import { can } from '../core/access';
import { esc } from '../ui/modal';
import { openClickerLogWindow } from './clicker-info-modal';
import { openClickerOperationsWindow } from './clicker-operations-modal';
import { openClickerHistoryWindow } from './clicker-history-modal';
import { openCloseShiftModal } from './close-shift-modal';
import { SHIFT_ICON } from './close-shift-shared';
import { fetchFarmClickerApps, appSupportsTask, clickerAppLabel, DEFAULT_TASK_TYPE } from './clicker-apps-shared';
import { recallClickerCreds, rememberClickerCreds } from './clicker-cred-memory';
import { amountGroupsRowHtml, readAmountBounds, readDepositBalance, wireAmountGroups } from './clicker-amount-fields';
import { flowFieldHtml, wireFlowField } from './clicker-flow-field';
import { syncDeviceSubtitlesIn } from '../devices/device-card';
import { t } from '../ui/lang';
import { PLAY_ICON, STOP_ICON, LOG_ICON, REGISTRY_ICON, HISTORY_ICON, confirmClickerStop, } from './clicker-shared';
/** Build the per-device launch params form + Start/Stop control inside a panel. */
export function renderDeviceLaunchPanel(panel, farmId, udid, cfg, status) {
    const c = cfg ?? {
        pin: '', password: '', bucket_code: '', operators_group: '', tg_chats: '',
        max_initial_amount: null, min_initial_amount: null,
        deposit_min_balance: null, deposit_max_balance: null,
        task_type: DEFAULT_TASK_TYPE, app_name: '',
    };
    panel.innerHTML = `
        <form class="clicker-device-form" novalidate>
            <div class="modal__field">
                <label>${t('clicker.task_type')}</label>
                <select class="settings-input dc-type">
                    <option value="payout" ${c.task_type === 'payout' ? 'selected' : ''}>${t('clicker.task_payout')}</option>
                    <option value="deposit" ${c.task_type === 'deposit' ? 'selected' : ''}>${t('clicker.task_deposit')}</option>
                    <option value="auto" ${c.task_type === 'auto' ? 'selected' : ''}>${t('clicker.task_auto')}</option>
                </select>
            </div>
            <div class="modal__field">
                <label>${t('clicker.app')}</label>
                <select class="settings-input dc-app">
                    <option value="" disabled hidden selected>${t('clicker.select_app')}</option>
                </select>
            </div>
            ${flowFieldHtml()}
            ${can('clicker.secrets')
        ? `<div class="modal__field">
                <label>${t('clicker.pin')} <span class="req">*</span></label>
                <input class="settings-input dc-pin" type="text" autocomplete="off" value="${esc(c.pin)}" />
            </div>
            <div class="modal__field">
                <label>${t('clicker.app_password')} <span class="req">*</span></label>
                <input class="settings-input dc-password" type="text" autocomplete="off" placeholder="${t('clicker.app_password_placeholder')}" value="${esc(c.password)}" />
            </div>`
        : ''}
            ${can('clicker.routing')
        ? `<div class="modal__field">
                <label>${t('clicker.bucket_code')}</label>
                <input class="settings-input dc-bucket-code" type="text" autocomplete="off" placeholder="${t('clicker.bucket_code_override_placeholder')}" value="${esc(c.bucket_code)}" />
            </div>
            <div class="modal__field">
                <label>${t('clicker.operators_group')}</label>
                <input class="settings-input dc-operators-group" type="text" autocomplete="off" placeholder="${t('clicker.operators_group_override_placeholder')}" value="${esc(c.operators_group)}" />
            </div>
            <div class="modal__field">
                <label>${t('clicker.tg_chats')}</label>
                <input class="settings-input dc-tg-chats" type="text" autocomplete="off" placeholder="${t('clicker.tg_chats_override_placeholder')}" value="${esc(c.tg_chats)}" />
            </div>`
        : ''}
            ${amountGroupsRowHtml({
        min: c.min_initial_amount,
        max: c.max_initial_amount,
        minPlaceholder: t('clicker.farm_default'),
        maxPlaceholder: t('clicker.farm_default'),
    }, {
        min: c.deposit_min_balance,
        max: c.deposit_max_balance,
        minPlaceholder: t('clicker.farm_default'),
        maxPlaceholder: t('clicker.farm_default'),
    })}
            <div class="clicker-device-form__actions">
                <button type="submit" class="modal__submit">${t('common.save')}</button>
                <button type="button" class="clicker-icon-btn clicker-start-btn" title="${t('clicker.start_clicker')}" aria-label="${t('clicker.start_clicker')}">${PLAY_ICON}</button>
                <button type="button" class="clicker-icon-btn clicker-icon-btn--danger clicker-stop-btn" title="${t('clicker.stop_clicker')}" aria-label="${t('clicker.stop_clicker')}" disabled>${STOP_ICON}</button>
                ${can('device.clickerLog')
        ? `<button type="button" class="clicker-icon-btn clicker-icon-btn--neutral clicker-logs-btn" title="${t('clicker.stream_logs')}" aria-label="${t('clicker.stream_logs')}">${LOG_ICON}</button>`
        : ''}
                <button type="button" class="clicker-icon-btn clicker-icon-btn--neutral clicker-ops-btn" title="${t('clicker.operations_registry')}" aria-label="${t('clicker.operations_registry')}">${REGISTRY_ICON}</button>
                ${can('clicker.closeShift')
        ? `<button type="button" class="clicker-icon-btn clicker-icon-btn--neutral clicker-shift-btn" title="${t('shifts.close_shift')}" aria-label="${t('shifts.close_shift')}">${SHIFT_ICON}</button>`
        : ''}
                ${can('device.clickerHistory')
        ? `<button type="button" class="clicker-icon-btn clicker-icon-btn--neutral clicker-history-btn" title="${t('clicker.history')}" aria-label="${t('clicker.history')}">${HISTORY_ICON}</button>`
        : ''}
            </div>
            <div class="settings-hint dc-launch-hint" style="display:none"></div>
            <div class="settings-error modal__error dc-error" style="display:none"></div>
        </form>
    `;
    wireAmountGroups(panel);
    const form = panel.querySelector('.clicker-device-form');
    // PIN and App password (clicker.secrets) are shown to any authenticated user;
    // Bucket code, Operators group and Telegram chats (clicker.routing) only to
    // admins. Either block can be absent, so every query below stays null-guarded —
    // an omitted field is simply not sent, and the server keeps the stored value.
    const pinInput = panel.querySelector('.dc-pin');
    const passwordInput = panel.querySelector('.dc-password');
    const bucketCodeInput = panel.querySelector('.dc-bucket-code');
    const operatorsGroupInput = panel.querySelector('.dc-operators-group');
    const tgChatsInput = panel.querySelector('.dc-tg-chats');
    const typeSel = panel.querySelector('.dc-type');
    const appSel = panel.querySelector('.dc-app');
    const errorEl = panel.querySelector('.dc-error');
    const hintEl = panel.querySelector('.dc-launch-hint');
    const startBtn = panel.querySelector('.clicker-start-btn');
    const stopBtn = panel.querySelector('.clicker-stop-btn');
    const logsBtn = panel.querySelector('.clicker-logs-btn'); // admin-only; absent for operators
    const shiftBtn = panel.querySelector('.clicker-shift-btn');
    // The App dropdown is driven by the apps available to this
    // device's farm team for the SELECTED task type (server filters the catalog by
    // the team matrix; an empty matrix = all apps). Re-populate on task-type change.
    // Lists only real apps; when nothing is selected (or the saved app is no longer
    // offered) it falls back to a disabled "Select app" placeholder so the user must
    // pick explicitly — we never send an empty/placeholder value as an app name.
    let availableApps = [];
    const repopulateApps = () => {
        const task = typeSel.value;
        const prev = appSel.value;
        const opts = [`<option value="" disabled hidden selected>${t('clicker.select_app')}</option>`];
        for (const a of availableApps) {
            if (!appSupportsTask(a, task))
                continue;
            opts.push(`<option value="${esc(a.name)}">${esc(clickerAppLabel(a))}</option>`);
        }
        appSel.innerHTML = opts.join('');
        // Restore prior selection if still offered (else fall back to the placeholder).
        appSel.value = Array.from(appSel.options).some((o) => o.value === prev) ? prev : '';
    };
    // Each app carries its own PIN/password (task type is NOT part of the key). Seed the
    // memory from the device's own saved config so switching away and back restores it,
    // then swap PIN/password to the selected app's remembered secrets on every app change
    // (clearing them when that app has none, so one app's secret never lingers onto another).
    if ((pinInput || passwordInput) && c.app_name)
        rememberClickerCreds(farmId, udid, c.app_name, c.pin, c.password);
    const applyRemembered = () => {
        if (!pinInput && !passwordInput)
            return;
        const creds = appSel.value ? recallClickerCreds(farmId, udid, appSel.value) : undefined;
        if (pinInput)
            pinInput.value = creds?.pin ?? '';
        if (passwordInput)
            passwordInput.value = creds?.password ?? '';
    };
    // Which flow this device runs — the same picker the reconfigure modal shows, listed for
    // the (task type, app) pair chosen above and limited to what this farm's team may use.
    const flowField = wireFlowField(panel, {
        farmId,
        initial: c.payout_flow_id ?? null,
        initialBuiltIn: c.built_in_flow,
        picks: c.flow_picks ?? {},
        typeSel,
        appSel,
    });
    typeSel.addEventListener('change', () => { repopulateApps(); flowField.repopulate(); });
    appSel.addEventListener('change', () => { applyRemembered(); flowField.repopulate(); });
    void fetchFarmClickerApps(farmId)
        .then((apps) => { availableApps = apps; })
        .catch(() => { availableApps = []; })
        .finally(() => {
        repopulateApps();
        // Apply the saved app once the list is loaded (it may have been filtered out).
        if (c.app_name && Array.from(appSel.options).some((o) => o.value === c.app_name)) {
            appSel.value = c.app_name;
        }
        // The flow list is keyed on the app, and the app is only settled here — the two
        // fetches race, so re-filter once the selection actually holds.
        flowField.repopulate();
    });
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        errorEl.style.display = 'none';
        const pin = pinInput ? pinInput.value.trim() : '';
        const password = passwordInput ? passwordInput.value.trim() : '';
        if (pinInput && !pin) {
            errorEl.textContent = t('clicker.pin_required');
            errorEl.style.display = 'block';
            return;
        }
        if (passwordInput && !password) {
            errorEl.textContent = t('clicker.app_password_required');
            errorEl.style.display = 'block';
            return;
        }
        const bounds = readAmountBounds(panel);
        if ('error' in bounds) {
            errorEl.textContent = bounds.error;
            errorEl.style.display = 'block';
            return;
        }
        const balance = readDepositBalance(panel);
        if ('error' in balance) {
            errorEl.textContent = balance.error;
            errorEl.style.display = 'block';
            return;
        }
        const btn = form.querySelector('.modal__submit');
        btn.disabled = true;
        try {
            const r = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(udid)}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    // Send the secret fields when present in the DOM (now always, for
                    // any authenticated user); omit if ever hidden so saved values persist.
                    ...(pinInput ? { pin } : {}),
                    ...(passwordInput ? { password } : {}),
                    ...(bucketCodeInput ? { bucket_code: bucketCodeInput.value.trim() } : {}),
                    ...(operatorsGroupInput ? { operators_group: operatorsGroupInput.value.trim() } : {}),
                    ...(tgChatsInput ? { tg_chats: tgChatsInput.value.trim() } : {}),
                    max_initial_amount: bounds.max,
                    min_initial_amount: bounds.min,
                    deposit_min_balance: balance.min,
                    deposit_max_balance: balance.max,
                    task_type: typeSel.value,
                    app_name: appSel.value,
                    ...flowField.picked(),
                }),
            });
            if (!r.ok) {
                errorEl.textContent = (await r.json()).error ?? t('common.failed_to_save');
                errorEl.style.display = 'block';
                return;
            }
            // Reflect the just-saved PIN in the launch gate (only when this role edits it;
            // operators omit the field and keep the server-side value untouched).
            if (pinInput)
                savedPin = pin;
            // Remember these secrets for this device's app (per-device, not shared).
            if (pinInput || passwordInput)
                rememberClickerCreds(farmId, udid, appSel.value, pin, password);
            refreshControls();
            const original = btn.textContent;
            btn.textContent = t('common.saved');
            setTimeout(() => { btn.textContent = original; }, 1500);
        }
        catch {
            errorEl.textContent = t('clicker.network_error');
            errorEl.style.display = 'block';
        }
        finally {
            btn.disabled = false;
        }
    });
    // Start/Stop: drive the clicker on the farm via the relay transport. The
    // launch params used are the SAVED ones (built server-side from the DB), so
    // Save before Start if you just edited a field.
    // Launch readiness: the server rejects a device with no saved PIN before the request
    // ever reaches the runner (buildClickerLaunch → "PIN is required to launch"). The saved
    // PIN is in `c.pin` for every role (it isn't redacted), so gate Start on it and tell the
    // user what's missing BEFORE they click — the server 400 stays as the fallback.
    let running = status?.state === 'running';
    // Asked to stop, farm has not confirmed: both buttons stay down until it does. Pressing
    // Stop again sends a kill that is already in flight, and Start would race the one that
    // is still coming — so the panel says what it is waiting for instead.
    let stopping = !running && !!status?.stopping;
    let savedPin = (c.pin ?? '').trim();
    const refreshControls = () => {
        const ready = savedPin !== '';
        startBtn.disabled = running || stopping || !ready;
        stopBtn.disabled = !running || stopping;
        // Closing a shift needs the phone to itself, so it is offered only while the clicker is down.
        if (shiftBtn) {
            shiftBtn.disabled = running || stopping;
            shiftBtn.title = running || stopping ? t('shifts.close_shift_busy') : t('shifts.close_shift');
        }
        const showHint = stopping || (!ready && !running);
        hintEl.style.display = showHint ? 'block' : 'none';
        if (showHint) {
            hintEl.textContent = stopping
                ? t('clicker.hint_stopping')
                : pinInput
                    ? t('clicker.hint_set_pin')
                    : t('clicker.hint_not_configured');
        }
        const card = panel.closest('.clicker-device');
        card?.classList.toggle('clicker-device--running', running);
        // Keep the name's "App · task" subtitle in sync with the optimistic start/stop
        // flip, so a running clicker shows it immediately (not only after the list poll).
        const nameWrap = card?.querySelector('.clicker-device-row__name-wrap');
        if (nameWrap) {
            syncDeviceSubtitlesIn(nameWrap, { clickerRunning: running, clickerApp: c.app_name, clickerTask: c.task_type });
        }
    };
    refreshControls();
    startBtn.addEventListener('click', async () => {
        errorEl.style.display = 'none';
        startBtn.disabled = true;
        try {
            const r = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(udid)}/start`, { method: 'POST' });
            if (!r.ok) {
                errorEl.textContent = (await r.json()).error ?? t('clicker.failed_to_start');
                errorEl.style.display = 'block';
                startBtn.disabled = false;
                return;
            }
            running = true;
            stopping = false;
            refreshControls();
        }
        catch {
            errorEl.textContent = t('clicker.network_error');
            errorEl.style.display = 'block';
            startBtn.disabled = false;
        }
    });
    stopBtn.addEventListener('click', async () => {
        if (!confirmClickerStop())
            return;
        errorEl.style.display = 'none';
        stopBtn.disabled = true;
        try {
            const r = await fetch(`/api/farms/${farmId}/clicker/devices/${encodeURIComponent(udid)}/stop`, { method: 'POST' });
            if (!r.ok) {
                errorEl.textContent = (await r.json()).error ?? t('clicker.failed_to_stop');
                errorEl.style.display = 'block';
                stopBtn.disabled = false;
                return;
            }
            running = false;
            stopping = true; // the farm still has to confirm the run is over
            refreshControls();
        }
        catch {
            errorEl.textContent = t('clicker.network_error');
            errorEl.style.display = 'block';
            stopBtn.disabled = false;
        }
    });
    logsBtn?.addEventListener('click', () => openClickerLogWindow(udid));
    shiftBtn?.addEventListener('click', () => openCloseShiftModal(farmId, udid));
    const opsBtn = panel.querySelector('.clicker-ops-btn');
    opsBtn.addEventListener('click', () => openClickerOperationsWindow(farmId, udid));
    // Run history (who started/stopped, when, with which params) — admin-only, so the
    // button is absent for lower roles.
    const historyBtn = panel.querySelector('.clicker-history-btn');
    historyBtn?.addEventListener('click', () => openClickerHistoryWindow(farmId, udid));
}
